// `$.ajax` did four things for us that `fetch` does not, and each one has to be replaced
// deliberately rather than assumed:
//
//   1. It attached the CSRF token, from the jQuery `ajaxPrefilter` rails-ujs installs. Only
//      matters for non-GET requests; `sendJSON` reads it from the meta tag itself. The two
//      ticket-scanner POSTs still stay on jQuery for another reason -- see the note in
//      ticketReducer.js.
//   2. It routed every failure through application.js's global `ajaxError` flash. It does not
//      fire for fetch, so each caller has to surface its own errors.
//   3. It treated a non-2xx response as an error. `fetch` only rejects on a network failure, so
//      that is done here.
//   4. It serialised array params as `name[]=1&name[]=2`, which is what Rails parses back into
//      an array.

export class RequestError extends Error {
  constructor(response) {
    super(response.statusText || `HTTP ${response.status}`);
    this.name = "RequestError";
    this.status = response.status;
    // Kept so a caller can read the error body the server sent, e.g. a 422's messages.
    this.response = response;
  }
}

const queryString = (params) => {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach((entry) => query.append(`${key}[]`, entry));
    } else {
      query.append(key, value);
    }
  });

  return query.toString();
};

// Rejects with a RequestError carrying `status`, so callers can treat a particular code as a
// control path the way they did with `xhr.status`.
export const getJSON = async (url, params = {}, { signal } = {}) => {
  const query = queryString(params);
  const separator = url.includes("?") ? "&" : "?";
  const requestUrl = query ? `${url}${separator}${query}` : url;

  // jQuery's `cache: false` appended a timestamp to the query string; this says the same thing
  // to the HTTP cache instead of to the URL.
  const response = await fetch(requestUrl, {
    headers: { Accept: "application/json" },
    cache: "no-store",
    signal,
  });

  if (!response.ok) throw new RequestError(response);

  return response.json();
};

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");

// For PUT/POST/DELETE. Resolves with the Response rather than a parsed body: the callers ask for
// different formats, or ignore the body, so reading it is left to them. `headers` is merged over
// the defaults, e.g. to send an `Accept`.
export const sendJSON = async (url, { method = "POST", body, headers = {} } = {}) => {
  const requestHeaders = {};
  const token = csrfToken();
  if (token) requestHeaders["X-CSRF-Token"] = token;
  if (body !== undefined) requestHeaders["Content-Type"] = "application/json";

  const response = await fetch(url, {
    method,
    headers: { ...requestHeaders, ...headers },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) throw new RequestError(response);

  return response;
};
